import { ReactElement, cloneElement, isValidElement } from 'react';

type ChildProps = {
  onClick?: React.MouseEventHandler<HTMLElement>;
};

/**
 * Outcome of a copy attempt.
 * `rich` - both the plain text and the `html` flavor reached the clipboard.
 * `plain` - only the plain text reached it, so any `html` flavor was dropped.
 * `failed` - nothing reached the clipboard.
 */
export type CopyToClipboardResult = 'rich' | 'plain' | 'failed';

type CopyToClipboardProps = {
  text: string;
  html?: string;
  onCopy?: (text: string, result: CopyToClipboardResult) => void;
  children: ReactElement<ChildProps>;
};

export const CopyToClipboard = ({
  text,
  html,
  onCopy,
  children,
}: CopyToClipboardProps) => {
  const handleClick: React.MouseEventHandler<HTMLElement> = async (event) => {
    if (isValidElement<ChildProps>(children) && children.props.onClick) {
      children.props.onClick(event);
    }

    const writePlain = async () => {
      try {
        await navigator.clipboard.writeText(text);
        onCopy?.(text, 'plain');
      } catch {
        onCopy?.(text, 'failed');
      }
    };

    if (!html || typeof ClipboardItem === 'undefined') {
      await writePlain();
      return;
    }

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      onCopy?.(text, 'rich');
    } catch {
      await writePlain();
    }
  };

  if (!isValidElement<ChildProps>(children)) {
    return children;
  }

  return cloneElement(children, {
    ...children.props,
    onClick: handleClick,
  });
};
